import { PageTitle, Btn } from "../components";
/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiDownload, apiFetch } from "../api";
import { useTenant } from "../context/TenantContext";
import { recordUiError } from "../observability";

const STATUS_LABELS = {
  PENDENTE: "Pendente",
  PREPARADO: "Preparado",
  ATRIBUIDO: "Atribuído",
  ABERTO_PELO_OPERADOR: "Aberto pelo operador",
  CONFIRMADO_MANUALMENTE: "Confirmado manualmente",
  FALHOU: "Falhou",
  CANCELADO: "Cancelado",
};

const STATUS_COLORS = {
  PENDENTE: "#a1a1aa",
  PREPARADO: "#60a5fa",
  ATRIBUIDO: "#f59e0b",
  ABERTO_PELO_OPERADOR: "#c084fc",
  CONFIRMADO_MANUALMENTE: "#22c55e",
  FALHOU: "#ef4444",
  CANCELADO: "#71717a",
};

const Card = ({ children, style }) => (
  <div style={{
    background: "var(--s2)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 14,
    ...style,
  }}>
    {children}
  </div>
);

const Button = ({ variant = "default", ...props }) => (
  <Btn {...props} variant={variant === "default" ? "secondary" : variant === "success" ? "primary" : variant} />
);

const parsePedidos = (value) => [...new Set(String(value || "")
  .split(/[,\n;]+/)
  .map((item) => item.trim())
  .filter(Boolean))];

const queryString = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== "" && value != null) search.set(key, value);
  });
  return search.toString();
};

const waMeFor = (recipient) => {
  const phone = String(recipient.telefone || "").replace(/\D/g, "");
  if (!phone) return null;
  return `https://wa.me/${phone}?text=${encodeURIComponent(recipient.mensagem || "")}`;
};

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString("pt-BR")
  : "—";

export default function DisparoManual() {
  const tenant = useTenant();
  const currentUserId = Number(tenant?.user_id || 0);
  const [pedidosInput, setPedidosInput] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("pedidos");
  const [status, setStatus] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ recipients: [], totals: {}, operators: [] });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const showError = useCallback((error) => {
    const text = error?.message || String(error || "Erro na Central de Contingência.");
    recordUiError({ message: text, page: "disparo_manual", source: "inline_error" });
    setMessage({ error: text });
  }, []);

  const baseFilters = useMemo(() => ({
    pedidos: mode === "pedidos" ? parsePedidos(pedidosInput).join(",") : "",
    data: mode === "data" ? date : "",
  }), [mode, pedidosInput, date]);

  const filters = useMemo(() => ({
    ...baseFilters,
    status,
    operador_id: operatorId,
    q: search.trim(),
  }), [baseFilters, status, operatorId, search]);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!baseFilters.pedidos && !baseFilters.data) {
      if (!silent) showError("Informe os pedidos ou selecione uma data.");
      return;
    }
    if (!silent) setLoading(true);
    try {
      const response = await apiFetch(
        `/contingencia-manual/destinatarios?${queryString(filters)}`
      );
      setData(response);
      setSelected((current) => new Set(
        [...current].filter((id) => response.recipients.some((row) => Number(row.id) === id))
      ));
      if (!silent) setMessage(null);
    } catch (error) {
      if (!silent) showError(error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [baseFilters, filters, showError]);

  useEffect(() => {
    if (!data.recipients.length) return undefined;
    const interval = window.setInterval(() => refresh({ silent: true }), 15000);
    return () => window.clearInterval(interval);
  }, [data.recipients.length, refresh]);

  const prepare = async () => {
    if (!baseFilters.pedidos && !baseFilters.data) {
      showError("Informe os pedidos ou selecione uma data.");
      return;
    }
    setLoading(true);
    try {
      const response = await apiFetch("/contingencia-manual/preparar", {
        method: "POST",
        body: JSON.stringify({
          pedidos: parsePedidos(pedidosInput),
          data: mode === "data" ? date : null,
        }),
      });
      setMessage({
        ok: `${response.grupos_preparados?.length || 0} grupo(s) preparado(s); `
          + `${response.falhas?.length || 0} falha(s). Nenhum envio foi registrado.`,
      });
      await refresh({ silent: true });
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const claim = async (ids) => {
    if (!ids.length) return;
    setLoading(true);
    try {
      const response = await apiFetch("/contingencia-manual/destinatarios/claim", {
        method: "POST",
        body: JSON.stringify({ ids }),
      });
      setMessage({
        ok: `${response.claimed?.length || 0} destinatário(s) assumido(s). `
          + `${response.conflicts?.length || 0} conflito(s).`,
      });
      await refresh({ silent: true });
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const action = async (recipient, acao, motivo = null) => {
    setLoading(true);
    try {
      await apiFetch(`/contingencia-manual/destinatarios/${recipient.id}/acao`, {
        method: "POST",
        body: JSON.stringify({ acao, motivo }),
      });
      setMessage({
        ok: acao === "CONFIRMAR"
          ? "Envio registrado como confirmação humana do operador."
          : "Ação registrada.",
      });
      await refresh({ silent: true });
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const release = async (recipient) => {
    setLoading(true);
    try {
      await apiFetch(`/contingencia-manual/destinatarios/${recipient.id}/liberar`, {
        method: "POST",
      });
      setMessage({ ok: "Atribuição liberada e auditada." });
      await refresh({ silent: true });
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const auditAction = async (recipient, acao, motivo = null) => {
    await apiFetch(`/contingencia-manual/destinatarios/${recipient.id}/acao`, {
      method: "POST",
      body: JSON.stringify({ acao, motivo }),
    });
  };

  const copy = async (recipient, field, label, auditCode) => {
    try {
      await navigator.clipboard.writeText(recipient[field] || "");
      await auditAction(recipient, auditCode);
      setMessage({ ok: `${label} copiado.` });
    } catch (error) {
      showError(error?.message || `Não foi possível copiar ${label.toLowerCase()}.`);
    }
  };

  const openContact = async (recipient) => {
    const href = waMeFor(recipient);
    if (!href) {
      showError("Destinatário sem telefone.");
      return;
    }
    const popup = window.open("", "_blank");
    if (popup) popup.opener = null;
    const opened = await action(recipient, "ABRIR");
    if (opened && popup) {
      popup.location = href;
    } else if (popup) {
      popup.close();
    } else if (opened) {
      showError("O navegador bloqueou a nova aba. Libere o popup e tente novamente.");
    }
  };

  const fail = (recipient) => {
    const reason = window.prompt("Motivo da falha de contato:");
    if (reason?.trim()) action(recipient, "FALHAR", reason.trim());
  };

  const cancel = (recipient) => {
    const reason = window.prompt("Justificativa do cancelamento:");
    if (reason?.trim()) action(recipient, "CANCELAR", reason.trim());
  };

  const registerDriverOrigin = async (recipient, response) => {
    const endpoint = recipient.metadata?.resposta_endpoint;
    if (!endpoint) return;
    setLoading(true);
    try {
      await auditAction(recipient, "REGISTRAR_ORIGEM_MOTORISTA", response);
      await apiFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({
          motorista_telefone: recipient.telefone,
          resposta: response,
        }),
      });
      setMessage({ ok: "Origem do motorista registrada e rota recalculada." });
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    if (!baseFilters.pedidos && !baseFilters.data) return;
    setLoading(true);
    try {
      const result = await apiDownload(
        `/contingencia-manual/export.csv?${queryString(baseFilters)}`
      );
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename;
      anchor.click();
      URL.revokeObjectURL(url);
      setMessage({ ok: "Exportação auditada concluída." });
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const reviewAndCloseRoute = async (routeId) => {
    setLoading(true);
    try {
      const summary = await apiFetch(`/rotas/${routeId}/resumo-comunicacao`);
      const detail = [
        `Automático: ${summary.confirmados_automatico}`,
        `Manual: ${summary.confirmados_manual}`,
        `Pendentes: ${summary.pendentes}`,
        `Falhas: ${summary.falhas}`,
      ].join(" · ");
      if (!summary.can_close) {
        showError(`Rota ${routeId} não pode ser fechada. ${detail}`);
        return;
      }
      if (!window.confirm(`Fechar a comunicação da rota ${routeId}?\n${detail}`)) return;
      await apiFetch(`/rotas/${routeId}/confirmar-envio`, { method: "POST" });
      setMessage({ ok: `Rota ${routeId} fechada com os totais auditados. ${detail}` });
      await refresh({ silent: true });
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const claimableRecipients = data.recipients.filter((row) => {
    const claimExpired = row.atribuicao_expira_em
      ? new Date(row.atribuicao_expira_em).getTime() <= Date.now()
      : false;
    return ["PREPARADO", "FALHOU"].includes(row.status) || claimExpired;
  });
  const allVisibleSelected = claimableRecipients.length > 0
    && claimableRecipients.every((row) => selected.has(Number(row.id)));
  const firstRecipientByRoute = new Map();
  data.recipients.forEach((row) => {
    if (!firstRecipientByRoute.has(Number(row.rota_id))) {
      firstRecipientByRoute.set(Number(row.rota_id), Number(row.id));
    }
  });

  const toggleAll = () => {
    setSelected(allVisibleSelected
      ? new Set()
      : new Set(claimableRecipients.map((row) => Number(row.id))));
  };

  return (
    <div style={{ padding: 20, color: "var(--text)", maxWidth: 1600, margin: "0 auto" }}>
      <div style={{ marginBottom: 14 }}>
        <PageTitle icon="warning">Central de contingência</PageTitle>
        <p style={{ color: "var(--muted)", fontSize: '0.9375rem' }}>
          Prepare as mensagens e abra o WhatsApp para enviar. “Confirmar envio manual” registra sua confirmação; a entrega não é verificada automaticamente.
        </p>
      </div>

      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={fieldStyle}>
            <span>Escopo</span>
            <select value={mode} onChange={(event) => setMode(event.target.value)} style={inputStyle}>
              <option value="pedidos">PGs/pedidos</option>
              <option value="data">Dia operacional</option>
            </select>
          </label>
          {mode === "pedidos" ? (
            <label style={{ ...fieldStyle, flex: "1 1 320px" }}>
              <span>Pedidos (PGs)</span>
              <input
                value={pedidosInput}
                onChange={(event) => setPedidosInput(event.target.value)}
                placeholder="2026/53834, 2026/53835 ou PG 42855"
                style={inputStyle}
              />
            </label>
          ) : (
            <label style={fieldStyle}>
              <span>Data</span>
              <input type="date" value={date} onChange={(event) => setDate(event.target.value)} style={inputStyle} />
            </label>
          )}
          <Button variant="primary" disabled={loading} onClick={prepare}>Preparar comunicação</Button>
          <Button disabled={loading} onClick={() => refresh()}>Atualizar</Button>
          <Button disabled={loading} onClick={exportCsv}>Exportar CSV</Button>
        </div>
        {message && (
          <div role={message.error ? "alert" : "status"} style={{
            marginTop: 10,
            fontSize: '0.9375rem',
            color: message.error ? "#f87171" : "#4ade80",
          }}>
            {message.error || message.ok}
          </div>
        )}
      </Card>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
        gap: 8,
        marginBottom: 12,
      }}>
        {[
          ["Pendentes", data.totals?.PENDENTES || 0],
          ["Atribuídos", data.totals?.ATRIBUIDOS || 0],
          ["Abertos", data.totals?.ABERTOS || 0],
          ["Confirmados", data.totals?.CONFIRMADOS_MANUALMENTE || 0],
          ["Falhas", data.totals?.FALHAS || 0],
          ["Total", data.totals?.TOTAL || 0],
        ].map(([label, value]) => (
          <Card key={label} style={{ padding: 10 }}>
            <div style={{ color: "var(--muted)", fontSize: '0.8125rem' }}>{label}</div>
            <div style={{ fontSize: '1.375rem', fontWeight: 700 }}>{value}</div>
          </Card>
        ))}
      </div>

      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={fieldStyle}>
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)} style={inputStyle}>
              <option value="">Todos</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label style={fieldStyle}>
            <span>Operador</span>
            <select value={operatorId} onChange={(event) => setOperatorId(event.target.value)} style={inputStyle}>
              <option value="">Todos</option>
              {(data.operators || []).map((operator) => (
                <option key={operator.id} value={operator.id}>{operator.nome}</option>
              ))}
            </select>
          </label>
          <label style={{ ...fieldStyle, flex: "1 1 220px" }}>
            <span>Nome ou telefone</span>
            <input value={search} onChange={(event) => setSearch(event.target.value)} style={inputStyle} />
          </label>
          <Button onClick={() => refresh()} disabled={loading}>Aplicar filtros</Button>
          <Button
            variant="primary"
            disabled={loading || selected.size === 0}
            onClick={() => claim([...selected])}
          >
            Assumir selecionados ({selected.size})
          </Button>
        </div>
      </Card>

      <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1180 }}>
          <thead>
            <tr style={{ background: "var(--s2)", textAlign: "left" }}>
              <th style={thStyle}><input type="checkbox" aria-label="Selecionar todos os destinatários disponíveis" checked={allVisibleSelected} onChange={toggleAll} /></th>
              <th style={thStyle}>PG / rota / grupo</th>
              <th style={thStyle}>Destinatário</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Responsável</th>
              <th style={thStyle}>Última ação</th>
              <th style={thStyle}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {data.recipients.map((recipient) => {
              const id = Number(recipient.id);
              const isMine = currentUserId
                ? Number(recipient.operador_id) === currentUserId
                : Boolean(recipient.operador_id);
              const claimExpired = recipient.atribuicao_expira_em
                ? new Date(recipient.atribuicao_expira_em).getTime() <= Date.now()
                : false;
              const actionable = ["ATRIBUIDO", "ABERTO_PELO_OPERADOR"].includes(recipient.status)
                && isMine
                && !claimExpired;
              const canClaim = ["PREPARADO", "FALHOU"].includes(recipient.status)
                || claimExpired;
              return (
                <tr key={recipient.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      aria-label={`Selecionar destinatário ${recipient.nome || recipient.id}`}
                      disabled={!canClaim}
                      checked={selected.has(id)}
                      onChange={() => setSelected((current) => {
                        const next = new Set(current);
                        if (next.has(id)) next.delete(id); else next.add(id);
                        return next;
                      })}
                    />
                  </td>
                  <td style={tdStyle}>
                    <strong>{recipient.id_pedido || "—"}</strong>
                    <div style={mutedStyle}>Rota {recipient.rota_id} · {recipient.grupo_label || `Grupo ${recipient.grupo_index + 1}`}</div>
                    {firstRecipientByRoute.get(Number(recipient.rota_id)) === id && (
                      <Button
                        disabled={loading}
                        style={{ marginTop: 5 }}
                        onClick={() => reviewAndCloseRoute(recipient.rota_id)}
                      >
                        Revisar fechamento
                      </Button>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <strong>{recipient.nome}</strong>
                    <div style={mutedStyle}>{recipient.destinatario_tipo} · {recipient.telefone || "sem telefone"}</div>
                    {(recipient.motivo_falha || recipient.motivo_cancelamento) && (
                      <div style={{ ...mutedStyle, color: "#f87171" }}>
                        {recipient.motivo_falha || recipient.motivo_cancelamento}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      color: STATUS_COLORS[recipient.status],
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                    }}>
                      {STATUS_LABELS[recipient.status] || recipient.status}
                    </span>
                    {recipient.status === "PENDENTE" && (
                      <div style={mutedStyle}>Prepare novamente antes de assumir.</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {recipient.operador_nome || "—"}
                    {recipient.atribuicao_expira_em && (
                      <div style={mutedStyle}>até {formatDateTime(recipient.atribuicao_expira_em)}</div>
                    )}
                  </td>
                  <td style={tdStyle}>{formatDateTime(recipient.ultima_acao_em)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {canClaim && (
                        <Button disabled={loading} onClick={() => claim([id])}>
                          {claimExpired
                            ? "Assumir claim expirado"
                            : recipient.status === "FALHOU" ? "Tentar novamente" : "Assumir"}
                        </Button>
                      )}
                      <Button
                        disabled={loading || !actionable}
                        onClick={() => copy(recipient, "mensagem", "Mensagem", "COPIAR_MENSAGEM")}
                      >
                        Copiar mensagem
                      </Button>
                      <Button
                        disabled={loading || !actionable || !recipient.link}
                        onClick={() => copy(recipient, "link", "Link", "COPIAR_LINK")}
                      >
                        Copiar link
                      </Button>
                      <Button disabled={loading || !actionable || !recipient.telefone} onClick={() => openContact(recipient)}>Abrir contato</Button>
                      {recipient.destinatario_tipo === "MOTORISTA" && actionable && (
                        <>
                          <Button disabled={loading} onClick={() => registerDriverOrigin(recipient, "1")}>
                            Origem: {recipient.metadata?.base_label || "Base"}
                          </Button>
                          <Button disabled={loading} onClick={() => registerDriverOrigin(recipient, "2")}>
                            Origem: residência
                          </Button>
                        </>
                      )}
                      <Button variant="success" disabled={loading || !actionable} onClick={() => action(recipient, "CONFIRMAR")}>
                        Confirmar envio manual
                      </Button>
                      <Button variant="danger" disabled={loading || !actionable} onClick={() => fail(recipient)}>Falhou</Button>
                      <Button disabled={loading || !actionable} onClick={() => cancel(recipient)}>Cancelar</Button>
                      {actionable && <Button disabled={loading} onClick={() => release(recipient)}>Liberar</Button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!data.recipients.length && (
              <tr>
                <td colSpan="7" style={{ ...tdStyle, textAlign: "center", color: "var(--muted)", padding: 28 }}>
                  Nenhum destinatário encontrado. Confira os pedidos, a data e os filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  color: "var(--muted)",
  fontSize: '0.9375rem',
};
const inputStyle = {
  minHeight: 44,
  padding: "10px 12px",
  border: "1px solid var(--border)",
  borderRadius: 8,
  background: "var(--s3)",
  color: "var(--text)",
};
const thStyle = { padding: "10px 8px", fontSize: '0.8125rem', color: "var(--muted)" };
const tdStyle = { padding: "9px 8px", fontSize: '0.9375rem', verticalAlign: "top" };
const mutedStyle = { color: "var(--muted)", fontSize: '0.8125rem', marginTop: 2 };
